import React, { createContext, useMemo } from 'react';
import useSWR from 'swr';
import { Provider } from '../../../types/global-provider.types';
import { useAuth } from '../../auth/auth.hook';
import { useConfig } from '../../config/config.hook';
import { useMatricula } from '../../matricula/matricula.hook';
import { consultaDetalheTurmaService } from '../services/consulta-detalhe-turma/consulta-detalhe-turma.service';
import { consultaTurmaPorIdService } from '../services/consultar-turma-por-id/consultar-turma-por-id.service';
import { DetalheTurmaHookResult, Props } from './turma-selecionada.types';

export const TurmaSelecionadaContext = createContext<DetalheTurmaHookResult>({
  turmaSelecionada: undefined,
  pendente: true,
  erro: false,
});

export const TurmaSelecionadaProvider: Provider<Props> = ({ children, idTurma }: Props) => {
  const { token } = useAuth();
  const { getSelecionada: getMatriculaSelecionada } = useMatricula();
  const urlBase = useConfig().getValueOrThrow<string>('API_DEDICADA');

  const matriculaSelecionada = getMatriculaSelecionada();

  const matricula = useMemo(() => {
    if (matriculaSelecionada) {
      return matriculaSelecionada.matricula;
    }
  }, [matriculaSelecionada]);

  const { data, isLoading, isValidating, error } = useSWR(
    token && idTurma ? ['turma-selecionada', idTurma, matricula] : null,
    async () => {
      const turma = consultaTurmaPorIdService({
        idTurma,
        urlBase,
        token,
        matricula: matriculaSelecionada?.matricula ?? null,
        periodo: matriculaSelecionada?.periodo ?? null,
      });
      const detalheTurma = consultaDetalheTurmaService({ idTurma, urlBase, token, matricula });

      const [turmaResponse, detalheTurmaResponse] = await Promise.all([turma, detalheTurma]);

      return {
        ...detalheTurmaResponse,
        turmaCursada: turmaResponse.cursada,
        periodoCursadoAluno: turmaResponse.periodoCursadoAluno,
      };
    },
    {
      revalidateOnMount: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      dedupingInterval: 180000,
    },
  );

  const resultado: DetalheTurmaHookResult = useMemo(
    () => ({ turmaSelecionada: data, pendente: isLoading || isValidating, erro: !!error }),
    [data, error, isLoading, isValidating],
  );

  return <TurmaSelecionadaContext.Provider value={resultado}>{children}</TurmaSelecionadaContext.Provider>;
};
